import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SkillModal } from './components/skill-modal.jsx';

const GET_SKILLS = `
  query GetSkills {
    skills {
      id
      name
      description
      parameters {
        name
        type
        required
        description
      }
      returns {
        type
        description
      }
    }
  }
`;

const DELETE_SKILL = `
  mutation DeleteSkill($id: ID!) {
    deleteSkill(id: $id)
  }
`;

export function SkillsManager() {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedSkill, setSelectedSkill] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [filter, setFilter] = React.useState('all');
  const queryClient = useQueryClient();

  const { data, isLoading: loading, error } = useQuery({
    queryKey: ['skills'],
    queryFn: async () => {
      // Import fetchGraphQL from graphql.js
      const { fetchGraphQL } = await import('../../../lib/graphql');
      const result = await fetchGraphQL(GET_SKILLS);
      console.log('Skills query result:', result);
      return result;
    }
  });

  const { mutate: deleteSkill } = useMutation({
    mutationFn: async (id) => {
      // Import fetchGraphQL from graphql.js
      const { fetchGraphQL } = await import('../../../lib/graphql');
      return fetchGraphQL(DELETE_SKILL, { id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
    onError: (error) => {
      console.error('Failed to delete skill:', error);
      // TODO: Add error toast notification
    }
  });

  const handleCreateSkill = () => {
    setSelectedSkill(null);
    setIsModalOpen(true);
  };

  const handleEditSkill = (skill) => {
    setSelectedSkill(skill);
    setIsModalOpen(true);
  };

  const handleDeleteSkill = async (id) => {
    if (window.confirm('Are you sure you want to delete this skill?')) {
      try {
        await deleteSkill(id);
      } catch (error) {
        console.error('Failed to delete skill:', error);
        // TODO: Add error toast notification
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSkill(null);
  };

  const filteredSkills = React.useMemo(() => {
    if (!data || !data.skills) {
      console.log('No skills data available:', data);
      return [];
    }

    return data.skills.filter((skill) => {
      const matchesSearch = skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        skill.description.toLowerCase().includes(searchQuery.toLowerCase());

      if (filter === 'all') return matchesSearch;
      // Add more filter conditions as needed
      return matchesSearch;
    });
  }, [data, searchQuery, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading skills: {error.message}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Skills Manager</h1>
        <button
          onClick={handleCreateSkill}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Skill
        </button>
      </div>

      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Search skills..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Skills</option>
          {/* Add more filter options as needed */}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSkills.map((skill) => (
          <div
            key={skill.id}
            className="border rounded-lg p-4 hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-medium">{skill.name}</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEditSkill(skill)}
                  className="p-1 text-gray-600 hover:text-blue-600"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDeleteSkill(skill.id)}
                  className="p-1 text-gray-600 hover:text-red-600"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="text-gray-600 mb-4">{skill.description}</p>
            <div className="space-y-2">
              <h4 className="font-medium">Parameters:</h4>
              <ul className="list-disc list-inside text-sm">
                {skill.parameters.map((param) => (
                  <li key={param.name} className="text-gray-600">
                    {param.name} ({param.type})
                    {param.required && <span className="text-red-500">*</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <SkillModal
          skill={selectedSkill}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleCloseModal}
        />
      )}
    </div>
  );
} 